import React from "react";
import { X, AlertCircle } from "lucide-react";
import { supabase } from "./supabaseClient";
import { CURRENT_SEMESTER } from "./config";

export default function CoursePopup({ course, onClose, onHighlight, highlighted, activeTab, onSelect, courseDetailsData, setSelectedCourse }) {
  if (!course) return null;

  // Ensure highlighted is an array
  const highlightedArray = Array.isArray(highlighted) ? highlighted : [];
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Check if any of the course codes are already highlighted
  const isSelected = course.course_codes &&
    (Array.isArray(course.course_codes)
      ? course.course_codes.some(code => highlightedArray.includes(code)) ||
        highlightedArray.includes(course.course_codes.join('/'))
      : highlightedArray.includes(course.course_codes));

  const handleSelectHistory = async () => {
    if (!course.course_codes) return;

    // Use onSelect (handleAddSelectedCourse) instead of onHighlight
    onSelect(course);
    onClose();

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

      if (!token) {
        console.error("No valid session token found");
        return;
      }

      const payload = {
        course_to_add: course.course_codes[0],
        semester: course.semester
      };

      // for cross-listed courses, this takes the first course code — be aware this
      // COULD cause issues later depending on treatment of cross-listed courses.
      const endpoint = isSelected ? "remove_course" : "add_course";
      try {
        const response = await fetch(`${backendUrl}/${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(payload), // no user_id
        });

        if (!response.ok) {
          throw new Error(`Backend error: ${response.status}`);
        }
      } catch (err) {
        console.error(`Error updating course history (${endpoint}):`, err);
      }
  };

  const instructor = course.faculty
    ? Object.values(course.faculty)?.[0]?.[0] || "Unavailable"
    : "Unavailable";
  const when = course.times_and_locations
    ? Object.values(Object.values(course.times_and_locations)?.[0] || {})[0]
        ?.map((s) => `${s.day} ${s.time}`)
        .join(", ") || "Unavailable"
    : "Unavailable";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel max-w-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
          <div>
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
              {course.course_codes && (Array.isArray(course.course_codes) ? course.course_codes : [course.course_codes]).map((code) => (
                <span key={code} className="badge-primary">{code}</span>
              ))}
              {course.semester && <span className="badge-neutral">{course.semester}</span>}
            </div>
            <h2 className="font-display text-2xl font-semibold leading-snug text-ink">
              {course.course_title || course.id}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 rounded-md p-1.5 text-ink-soft transition-colors hover:bg-surface-alt hover:text-ink"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* Historical / not-offered banner */}
          {course.semester && course.semester !== CURRENT_SEMESTER && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">Not offered Fall 2026</div>
                <div className="mt-0.5 text-amber-800">
                  This course was most recently taught in{" "}
                  <span className="font-medium">{course.semester}</span>. It's shown here
                  for reference so you can see how it relates to what is offered this fall.
                </div>
              </div>
            </div>
          )}

          {/* description_raw is the registrar's own wording; description has course codes,
              prerequisites and logistics stripped out for embedding. Students read the former. */}
          <p className="whitespace-pre-line leading-relaxed text-ink">
            {course.description_raw || course.description || "No course description available."}
          </p>

          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <dt className="label">Instructor</dt>
              <dd className="text-sm text-ink">{instructor}</dd>
            </div>
            <div>
              <dt className="label">When</dt>
              <dd className="text-sm text-ink">{when}</dd>
            </div>
          </dl>

          {(() => {
            const hasGroups = course.prereq_groups?.length > 0;
            const hasIP = !!course.instructor_permission_ok;
            const hasClassYear = course.class_year_restriction && course.class_year_restriction.length > 0;
            const noteText = course.prereq_unstructured_note && course.prereq_unstructured_note.trim();
            const noteIsJustIP = noteText && /instructor permission required\.?$/i.test(noteText);
            const hasExtraNote = noteText && !noteIsJustIP;
            const hasAnyContent = hasGroups || hasIP || hasClassYear || hasExtraNote || course.student_eligibility_rule;

            if (!hasAnyContent) return null;

            const notesItems = [];
            if (hasIP && hasGroups) {
              notesItems.push("Instructor permission is also accepted.");
            } else if (hasIP && !hasGroups) {
              notesItems.push("Instructor permission required — email the professor to enroll.");
            }
            if (hasClassYear) {
              notesItems.push(`Open to ${course.class_year_restriction.join(', ')} only.`);
            }
            if (hasExtraNote) {
              notesItems.push(noteText);
            }

            return (
              <>
                {/* Prerequisites — course-based requirements only */}
                {hasGroups && (
                  <div className="border-t border-line pt-4">
                    <h3 className="mb-2 font-display text-base font-semibold text-ink">Prerequisites</h3>
                    <ul className="space-y-2 text-sm text-ink">
                      {course.prereq_groups.map((group, i) => {
                        const codes = group.codes || [];
                        const isOr = codes.length > 1;
                        return (
                          <li key={i} className="flex items-start gap-2">
                            <span
                              className="mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                              style={{ background: 'rgba(212,136,106,0.18)', color: '#a3603f' }}
                            >
                              {course.prereq_groups.length > 1 ? `Group ${i + 1}` : 'Req'}
                            </span>
                            <span className="leading-snug">
                              {isOr ? (
                                <><span className="font-semibold">Any one of:</span> {codes.join(' or ')}</>
                              ) : (
                                <><span className="font-semibold">Required:</span> {codes[0]}</>
                              )}
                              {group.note && (
                                <span className="ml-1 text-ink-muted italic">({group.note})</span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {course.prereq_groups.length > 1 && (
                      <p className="mt-2 text-xs text-ink-muted italic">
                        All {course.prereq_groups.length} groups must be satisfied.
                      </p>
                    )}
                  </div>
                )}

                {/* Notes — restrictions, IP, class-year, exclusions */}
                {notesItems.length > 0 && (
                  <div className="border-t border-line pt-4">
                    <h3 className="mb-2 font-display text-base font-semibold text-ink">Notes</h3>
                    <ul className="space-y-1 text-sm text-ink">
                      {notesItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1 shrink-0 h-1 w-1 rounded-full bg-ink-muted" />
                          <span className="leading-snug">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              </>
            );
          })()}

          {course.similar_courses && course.similar_courses.length > 0 && (
            <div className="border-t border-line pt-4">
              <h3 className="mb-2 font-display text-base font-semibold text-ink">Similar courses</h3>
              <div className="space-y-1.5">
                {course.similar_courses.map((similar, index) => {
                  const similarCodes = Array.isArray(similar.code) ? similar.code : [similar.code];
                  const similarCourse = courseDetailsData.find(c => {
                    const courseCodes = Array.isArray(c.course_codes) ? c.course_codes : [c.course_codes];
                    return courseCodes.some(code => similarCodes.includes(code));
                  });
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        onClose();
                        if (similarCourse) setSelectedCourse(similarCourse);
                      }}
                      className="flex w-full items-baseline gap-2 rounded-lg border border-transparent px-3 py-2 text-left transition-colors hover:border-line hover:bg-surface-alt"
                    >
                      <span className="font-medium text-primary">
                        {similarCourse?.course_codes?.join(', ') ||
                         (Array.isArray(similar.code) ? similar.code.join(', ') : similar.code)}
                      </span>
                      <span className="text-sm text-ink-muted">
                        {similarCourse?.course_title || 'Course title unavailable'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer action */}
        {activeTab === 'thisSemester' && (
          <div className="flex justify-end border-t border-line px-6 py-4">
            {course.semester !== CURRENT_SEMESTER ? (
              <button
                onClick={handleSelectHistory}
                className={isSelected ? "btn-danger" : "btn-primary"}
              >
                {isSelected ? 'Remove from history' : `I took this in ${course.semester}`}
              </button>
            ) : (
              <button
                onClick={handleSelectHistory}
                className={isSelected ? "btn-danger" : "btn-primary"}
              >
                {isSelected ? 'Remove from my plan' : 'I might take this course'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
